import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { TextField, Button, Stack, Typography, Alert, Box, Autocomplete, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, MenuItem } from '@mui/material';
import { crearTramiteRTI } from '../../services/tramiteRTIService';
import { modificarTramite, getTramites, guardarMotivoComplementario } from '../../services/tramiteService';
import { buscarClientes, getClientePorCuil } from '../../services/clienteService';
import ModalRegistrarCliente from '../ModalRegistrarCliente';
import SeccionGenerarPacto from '../Pacto/SeccionGenerarPacto';
import usePactosPendientes from '../Pacto/usePactosPendientes';
import { useAuth } from '../../auth/AuthContext';
import { apellidoNombre } from '../../utils/nombrePersona';
import CampoFechaLiquidar from '../Tramite/CampoFechaLiquidar';
import PageHeader from '../ui/PageHeader';

/**
 * Formulario exclusivo para iniciar trámites del área RTI.
 * idTipoTramite = 1 (RTI) está hardcodeado — sin selector de tipo.
 */
// onIniciarComplementario: lo pasa la ficha de atención. Si está, "Sí, iniciar ART" no navega a la
// página suelta de alta (que no conoce el turno y dejaba el ART huérfano de la atención): le devuelve el
// RTI creado a la ficha, que abre el ART ahí mismo con el mismo turno de origen.
export default function FormularioIniciarTramiteRTI({ onSuccess, onCancel, cuilInicial, idTurnoOrigen, vieneDeOtroTramite = false, onIniciarComplementario }) {
    const navigate = useNavigate();
    const { registrarPacto, adjuntarATramite } = usePactosPendientes();
    const location = useLocation();
    const { user } = useAuth();
    const { cuil: cuilParam } = useParams();
    const cuilEfectivo = cuilInicial || cuilParam;
    const pendingTramiteId = location.state?.pendingTramiteId;
    const clienteInicial = location.state?.clienteInicial || location.state?.cliente;
    const turnoReferencia = location.state?.turnoReferencia;
    const vieneDeOtroTramiteState = Boolean(location.state?.vieneDeOtroTramite || location.state?.flujoCruzado);
    const bloquearCreacionComplementaria = Boolean(vieneDeOtroTramite || vieneDeOtroTramiteState);

    const ID_TIPO_RTI = 1;

    const [form, setForm] = useState({
        idCliente: '',
        fechaInicioEstimada: '',
        idTipoTramite: ID_TIPO_RTI,
        comentarios: '',
        idUsuario: 1,
        patologia: '',
        organismo: '',
        // Campos propios de la Carátula RTI (ver modulos/caratula)
        liq: '',
        cuota: '',
        ddjjSalud: false,
        aportesProvincia: false,
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Dialog complementario ART
    const [dialogoComplementario, setDialogoComplementario] = useState(false);
    const [tramiteRtiCreado, setTramiteRtiCreado] = useState(null);
    const [pasoComplementario, setPasoComplementario] = useState(1);
    const [motivoSinART, setMotivoSinART] = useState('');
    const [guardandoMotivo, setGuardandoMotivo] = useState(false);

    // --- Estados para la búsqueda de cliente ---
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [opcionesCliente, setOpcionesCliente] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [modalClienteOpen, setModalClienteOpen] = useState(false);
    const [tieneDuplicadoRTI, setTieneDuplicadoRTI] = useState(false);
    const [cargandoDuplicados, setCargandoDuplicados] = useState(false);
    const [confirmarDuplicado, setConfirmarDuplicado] = useState(false);

    // Carga inicial de cliente (si viene por URL)
    useEffect(() => {
        const cargarDatosIniciales = async () => {
            try {
                if (clienteInicial?.cuil || clienteInicial?.cuilCliente || clienteInicial?.dni) {
                    const cliente = clienteInicial;
                    setClienteSeleccionado(cliente);
                    setForm(prev => ({ ...prev, idCliente: cliente.cuil || cliente.cuilCliente || cliente.dni || '' }));
                    return;
                }
                if (cuilEfectivo) {
                    const cliente = await getClientePorCuil(cuilEfectivo);
                    setClienteSeleccionado(cliente);
                    setForm(prev => ({ ...prev, idCliente: cliente.cuil }));
                }
            } catch (err) {
                setError('Error al cargar datos iniciales.');
                console.error(err);
            }
        };
        cargarDatosIniciales();
    }, [cuilEfectivo, clienteInicial]);

    useEffect(() => {
        const cuilCliente = form.idCliente;
        if (!cuilCliente) {
            setTieneDuplicadoRTI(false);
            setConfirmarDuplicado(false);
            return;
        }

        let activo = true;

        const cargarDuplicados = async () => {
            setCargandoDuplicados(true);
            try {
                const tramites = await getTramites(0, 200, cuilCliente, null, null, '');
                const items = Array.isArray(tramites) ? tramites : (tramites?.content || []);

                const esTramiteRTI = (t) => {
                    const idTipo = Number(t?.idTipoTramite);
                    const nombreTipo = String(
                        t?.nombreTipoTramite || t?.tipoTramite?.nombre || t?.tipoTramite || ''
                    ).toLowerCase();
                    return idTipo === ID_TIPO_RTI || nombreTipo.includes('rti') || nombreTipo.includes('general');
                };

                const esTramiteActivo = (t) => {
                    const estado = String(t?.nombreEstadoActual || t?.estadoActual || '').toLowerCase();
                    if (!estado) return true;
                    return !['finaliz', 'cancel', 'rechaz', 'cerrad', 'baja'].some(token => estado.includes(token));
                };

                const existeDuplicado = items.some((t) =>
                    Number(t?.idTramite) !== Number(pendingTramiteId) && esTramiteRTI(t) && esTramiteActivo(t)
                );

                if (activo) {
                    setTieneDuplicadoRTI(existeDuplicado);
                    if (!existeDuplicado) {
                        setConfirmarDuplicado(false);
                    }
                }
            } catch (err) {
                console.error('Error validando trámites RTI duplicados:', err);
                if (activo) {
                    setTieneDuplicadoRTI(false);
                }
            } finally {
                if (activo) {
                    setCargandoDuplicados(false);
                }
            }
        };

        cargarDuplicados();
        return () => {
            activo = false;
        };
    }, [form.idCliente, pendingTramiteId]);

    const busquedaTimeoutRef = useRef(null);

    // Búsqueda de clientes con debounce: espera 300ms tras el último tecleo antes de llamar a la API
    const handleBusquedaCliente = (_event, value) => {
        if (!value) { setOpcionesCliente([]); setBuscando(false); return; }
        setBuscando(true);
        if (busquedaTimeoutRef.current) clearTimeout(busquedaTimeoutRef.current);
        busquedaTimeoutRef.current = setTimeout(async () => {
            try {
                const resultados = await buscarClientes(value);
                setOpcionesCliente(resultados);
            } catch (error) {
                console.error("Error al buscar clientes", error);
            } finally {
                setBuscando(false);
            }
        }, 300);
    };

    const handleSeleccionCliente = (event, newValue) => {
        setClienteSeleccionado(newValue);
        setForm(prev => ({ ...prev, idCliente: newValue ? newValue.cuil : '' }));
    };

    // Manejo del modal de registro
    const handleRegistroExitoso = (clienteNuevo) => {
        setClienteSeleccionado(clienteNuevo);
        setForm(prev => ({ ...prev, idCliente: clienteNuevo.cuil }));
        setModalClienteOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleChangeCheckbox = (e) => {
        const { name, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: checked }));
    };

    const finalizarFlujo = (tramite) => {
        if (onSuccess) {
            onSuccess(tramite);
            return;
        }
        navigate(`/rti/tramites/${tramite.idTramite}`);
    };

    const abrirDialogoComplementario = (tramiteCreado) => {
        setTramiteRtiCreado(tramiteCreado);
        setPasoComplementario(1);
        setMotivoSinART('');
        setDialogoComplementario(true);
    };

    const generarFirmaComentario = () => {
        const ahora = new Date();
        const dd = String(ahora.getDate()).padStart(2, '0');
        const mm = String(ahora.getMonth() + 1).padStart(2, '0');
        const yyyy = ahora.getFullYear();
        const hh = String(ahora.getHours()).padStart(2, '0');
        const min = String(ahora.getMinutes()).padStart(2, '0');
        return `[${dd}/${mm}/${yyyy} ${hh}:${min} - ${user?.nombre || 'Usuario'}]:`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.idCliente) {
            setError('Debe seleccionar un cliente.');
            return;
        }
        if (tieneDuplicadoRTI && !confirmarDuplicado) {
            setError('Debe confirmar que desea crear un nuevo trámite RTI para un cliente con trámite activo.');
            return;
        }
        setError('');
        setSuccess('');
        try {
            if (pendingTramiteId) {
                const rolUsuario = String(user?.rol || '')
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toUpperCase();
                const comentarioInicio = rolUsuario === 'RECEPCION'
                    ? `Tramite iniciado por RECEPCION${form.comentarios ? ` | ${form.comentarios}` : ''}`
                    : form.comentarios;

                const tramiteActualizado = await modificarTramite(pendingTramiteId, {
                    idTipoTramite: ID_TIPO_RTI,
                    comentarios: comentarioInicio,
                    idCliente: form.idCliente,
                    idUsuario: form.idUsuario
                });
                if (window.recargarCampanaNotificaciones) {
                    window.recargarCampanaNotificaciones();
                }
                // El pacto se emitió antes de que el trámite existiera: recién ahora se puede adjuntar.
                await adjuntarATramite(tramiteActualizado.idTramite);
                setSuccess(`Trámite RTI iniciado con éxito. ID: ${tramiteActualizado.idTramite}`);
                if (bloquearCreacionComplementaria) {
                    finalizarFlujo(tramiteActualizado);
                } else {
                    abrirDialogoComplementario(tramiteActualizado);
                }
            } else {
                const tramiteNuevo = await crearTramiteRTI({
                    ...form,
                    organismo: form.organismo || null,
                    fechaInicioEstimada: form.fechaInicioEstimada || null,
                    idTurnoOrigen: idTurnoOrigen || null,
                });
                if (window.recargarCampanaNotificaciones) {
                    window.recargarCampanaNotificaciones();
                }
                // El pacto se emitió antes de que el trámite existiera: recién ahora se puede adjuntar.
                await adjuntarATramite(tramiteNuevo.idTramite);
                setSuccess(`Trámite RTI creado con éxito. ID: ${tramiteNuevo.idTramite}`);
                if (bloquearCreacionComplementaria) {
                    finalizarFlujo(tramiteNuevo);
                } else {
                    abrirDialogoComplementario(tramiteNuevo);
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error al crear el trámite.');
        }
    };

    return (
        <div className="area-rti">
            <Box sx={{
                background: "var(--bg-page)",
                minHeight: "100vh",
                padding: 3
            }}>
                <Box sx={{
                    maxWidth: 900,
                    margin: 'auto',
                    background: "white",
                    borderRadius: "16px",
                    boxShadow: "var(--shadow-md)",
                    overflow: "hidden"
                }}>
                    <PageHeader
                        variante="banner"
                        icono="file-earmark-plus"
                        titulo="Iniciar Trámite RTI"
                        descripcion="Complete los datos para registrar un nuevo trámite RTI"
                    />

                    {/* Formulario */}
                    <Box sx={{ padding: "2rem" }}>
                        <ModalRegistrarCliente
                            open={modalClienteOpen}
                            onClose={() => setModalClienteOpen(false)}
                            onSuccess={handleRegistroExitoso}
                            mostrarDialogoConfirmacion={false}
                        />

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={3}>
                                {/* Sección Seleccionar Cliente */}
                                <div style={{
                                    background: "var(--gradient-primary-light)",
                                    padding: "1rem 1.5rem",
                                    borderRadius: "12px",
                                    border: "2px solid #bfdbfe"
                                }}>
                                    <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)' }}>
                                        <i className="bi bi-person-check me-2"></i>Seleccionar Cliente
                                    </Typography>
                                </div>

                                {turnoReferencia && (
                                    <Alert severity="info" sx={{ borderRadius: '10px' }}>
                                        Se está iniciando un trámite a partir del turno #{turnoReferencia.numeroIdentificacion || turnoReferencia.idTurno}.
                                    </Alert>
                                )}

                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                    <Autocomplete
                                        id="buscar-cliente-autocomplete"
                                        options={opcionesCliente}
                                        getOptionLabel={(option) => `${apellidoNombre(option.apellido, option.nombre)} (${option.cuil})`}
                                        onInputChange={handleBusquedaCliente}
                                        onChange={handleSeleccionCliente}
                                        value={clienteSeleccionado}
                                        isOptionEqualToValue={(option, value) => option.cuil === value.cuil}
                                        noOptionsText="No se encontraron clientes"
                                        loading={buscando}
                                        loadingText="Buscando..."
                                        sx={{ flexGrow: 1 }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Buscar cliente por CUIL, Apellido o Nombre"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {buscando ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                    <Button
                                        className="btn btn-outline-primary shadow-sm fw-semibold px-4"
                                        variant="outlined"
                                        onClick={() => setModalClienteOpen(true)}
                                        sx={{ height: '56px', flexShrink: 0 }}
                                    >
                                        Registrar Cliente
                                    </Button>
                                </Box>

                                <SeccionGenerarPacto cliente={clienteSeleccionado} onPactoGenerado={registrarPacto} />

                                {/* Sección Datos del Trámite RTI */}
                                <div style={{
                                    background: "var(--gradient-primary-light)",
                                    padding: "1rem 1.5rem",
                                    borderRadius: "12px",
                                    border: "2px solid #bfdbfe",
                                    marginTop: "1.5rem"
                                }}>
                                    <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)' }}>
                                        <i className="bi bi-file-earmark-text me-2"></i>Datos del Trámite RTI
                                    </Typography>
                                </div>

                                {/* Organismo */}
                                <TextField
                                    select
                                    label="Organismo"
                                    name="organismo"
                                    value={form.organismo}
                                    onChange={handleChange}
                                    fullWidth
                                    helperText="Seleccione si el trámite se gestiona ante Provincia o ANSES."
                                >
                                    <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                    <MenuItem value="PROVINCIA">Provincia</MenuItem>
                                    <MenuItem value="ANSES">ANSES</MenuItem>
                                </TextField>

                                {/* Patología — campo específico de RTI */}
                                <TextField
                                    label="Patología"
                                    name="patologia"
                                    value={form.patologia}
                                    onChange={handleChange}
                                    fullWidth
                                />

                                {/* LIQ / CUOTA / DDJJ de salud / Aportes provincia — campos de la Carátula RTI */}
                                <Stack direction="row" spacing={2}>
                                    <TextField
                                        label="LIQ"
                                        name="liq"
                                        value={form.liq}
                                        onChange={handleChange}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Cuota"
                                        name="cuota"
                                        value={form.cuota}
                                        onChange={handleChange}
                                        fullWidth
                                    />
                                </Stack>
                                <Stack direction="row" spacing={3}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="ddjjSalud"
                                                checked={form.ddjjSalud}
                                                onChange={handleChangeCheckbox}
                                            />
                                        }
                                        label="DDJJ de salud presentada"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="aportesProvincia"
                                                checked={form.aportesProvincia}
                                                onChange={handleChangeCheckbox}
                                            />
                                        }
                                        label="Aporta en provincia"
                                    />
                                </Stack>

                                <CampoFechaLiquidar
                                    valor={form.fechaInicioEstimada}
                                    onChange={(v) => setForm((f) => ({ ...f, fechaInicioEstimada: v }))}
                                />

                                <TextField
                                    label="Comentarios Iniciales"
                                    name="comentarios"
                                    value={form.comentarios}
                                    onChange={handleChange}
                                    multiline
                                    rows={4}
                                    fullWidth
                                />

                                {error && <Alert severity="error" sx={{borderRadius: "10px"}}>{error}</Alert>}
                                {success && <Alert severity="success" sx={{borderRadius: "10px"}}>{success}</Alert>}
                                {tieneDuplicadoRTI && (
                                    <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                        Este cliente ya posee un trámite de RTI en el sistema. ¿Está seguro de que desea crear otro?
                                    </Alert>
                                )}
                                {tieneDuplicadoRTI && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={confirmarDuplicado}
                                                onChange={(e) => setConfirmarDuplicado(e.target.checked)}
                                            />
                                        }
                                        label="Confirmo que deseo continuar con un nuevo trámite RTI igualmente."
                                    />
                                )}
                                {cargandoDuplicados && <Alert severity="info" sx={{ borderRadius: '10px' }}>Verificando trámites activos del cliente...</Alert>}

                                <Stack direction="row" spacing={2} sx={{ width: '100%', marginTop: "2rem" }}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        size="large"
                                        onClick={() => onCancel ? onCancel() : navigate('/rti/tramites')}
                                        sx={{
                                            background: "white",
                                            color: "#dc2626",
                                            border: "2px solid #fecaca",
                                            borderRadius: "10px",
                                            padding: "0.75rem",
                                            fontWeight: 500,
                                            textTransform: "none",
                                            "&:hover": {
                                                background: "#fef2f2",
                                                border: "2px solid #fca5a5"
                                            }
                                        }}
                                    >
                                        <i className="bi bi-x-circle me-2"></i>
                                        Cancelar
                                    </Button>
                                    <Button
                                        fullWidth
                                        type="submit"
                                        size="large"
                                        disabled={!form.idCliente}
                                        sx={{
                                            background: "var(--primary)",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "10px",
                                            padding: "0.75rem",
                                            fontWeight: 600,
                                            textTransform: "none",
                                            boxShadow: "var(--shadow-md)",
                                            "&:hover": {
                                                background: "var(--primary)",
                                                boxShadow: "var(--shadow-md)"
                                            },
                                            "&:disabled": {
                                                background: "#e5e7eb",
                                                color: "#9ca3af"
                                            }
                                        }}
                                    >
                                        <i className="bi bi-check-circle me-2"></i>
                                        Iniciar Trámite RTI
                                    </Button>
                                </Stack>
                            </Stack>
                        </form>
                    </Box>
                </Box>
            </Box>

            {/* Dialog complementario ART */}
            <Dialog open={dialogoComplementario} maxWidth="sm" fullWidth className="dialog-centrado-contenido" PaperProps={{ sx: { borderRadius: '16px' } }}>
                {pasoComplementario === 1 && (
                    <>
                        <DialogTitle sx={{ fontWeight: 'bold', color: '#15803d', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <i className="bi bi-check-circle-fill" style={{ color: '#22c55e' }}></i>
                            Trámite RTI creado
                        </DialogTitle>
                        <DialogContent>
                            <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>
                                Trámite RTI #{tramiteRtiCreado?.idTramite} creado con éxito.
                            </Alert>
                            <Typography variant="body1" sx={{ color: '#374151' }}>
                                ¿Desea iniciar también un trámite <strong>ART</strong> para este cliente?
                            </Typography>
                        </DialogContent>
                        <DialogActions sx={{ p: 2, gap: 1 }}>
                            <Button onClick={() => setPasoComplementario(2)} sx={{ color: '#6b7280', textTransform: 'none' }}>
                                No
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setDialogoComplementario(false);
                                    if (onIniciarComplementario) {
                                        onIniciarComplementario(tramiteRtiCreado);
                                        return;
                                    }
                                    if (onCancel) {
                                        onCancel();
                                    }
                                    const cuilCliente = tramiteRtiCreado?.cliente?.cuil || tramiteRtiCreado?.idCliente || form.idCliente;
                                    navigate(`/art/tramites/iniciar-tramite/${cuilCliente}`, {
                                        state: {
                                            cuil: cuilCliente,
                                            vieneDeOtroTramite: true,
                                            flujoCruzado: true,
                                            areaOrigen: 'RTI',
                                            tramiteOrigenId: tramiteRtiCreado?.idTramite,
                                        }
                                    });
                                }}
                                sx={{ background: 'var(--primary)', borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                            >
                                <i className="bi bi-heart-pulse me-2"></i>
                                Sí, iniciar ART
                            </Button>
                        </DialogActions>
                    </>
                )}
                {pasoComplementario === 2 && (
                    <>
                        <DialogTitle sx={{ fontWeight: 'bold', color: '#1e40af' }}>
                            Motivo por el cual no se inicia ART
                        </DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" color="#64748b" sx={{ mb: 2 }}>
                                Indique el motivo por el cual no se inicia también el trámite ART. Quedará registrado en el trámite RTI.
                            </Typography>
                            <TextField
                                label="Motivo"
                                value={motivoSinART}
                                onChange={(e) => setMotivoSinART(e.target.value)}
                                multiline
                                rows={3}
                                fullWidth
                            />
                        </DialogContent>
                        <DialogActions sx={{ p: 2, gap: 1 }}>
                            <Button onClick={() => setPasoComplementario(1)} sx={{ color: '#6b7280', textTransform: 'none' }}>
                                <i className="bi bi-arrow-left me-1"></i> Volver
                            </Button>
                            <Button onClick={() => { setDialogoComplementario(false); finalizarFlujo(tramiteRtiCreado); }} sx={{ color: '#6b7280', textTransform: 'none' }}>
                                Omitir y continuar
                            </Button>
                            <Button
                                variant="contained"
                                disabled={!motivoSinART.trim() || guardandoMotivo}
                                onClick={async () => {
                                    setGuardandoMotivo(true);
                                    try {
                                        const firma = generarFirmaComentario();
                                        const { tramiteActualizado, comentariosActualizados } = await guardarMotivoComplementario({
                                            idTramite: tramiteRtiCreado.idTramite,
                                            idCliente: tramiteRtiCreado?.idCliente || form.idCliente,
                                            idTipoTramite: ID_TIPO_RTI,
                                            idUsuario: form.idUsuario,
                                            versionFallback: tramiteRtiCreado?.version ?? 0,
                                            indicador: '[MOTIVO NO INICIO ART]',
                                            motivo: motivoSinART.trim(),
                                            firma,
                                            comentariosFallback: (form.comentarios ?? '').trim()
                                                || (tramiteRtiCreado?.comentarios ?? '').trim()
                                                || (tramiteRtiCreado?.comentario ?? '').trim()
                                        });
                                        setDialogoComplementario(false);
                                        finalizarFlujo({
                                            ...tramiteRtiCreado,
                                            ...tramiteActualizado,
                                            comentarios: comentariosActualizados
                                        });
                                    } catch (err) {
                                        console.error('Error guardando motivo:', err);
                                        setError('No se pudo guardar el motivo por un conflicto de edición. Intente nuevamente.');
                                    } finally {
                                        setGuardandoMotivo(false);
                                    }
                                }}
                                sx={{ background: 'var(--primary)', borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                            >
                                {guardandoMotivo ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Confirmar'}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </div>
    );
}